/**
 * Proportions generator.
 *
 * A proportion a/b = c/d with one unknown. Choose the solution first, then
 * build a true proportion around it; cross-multiplication yields a linear
 * equation whose validated canonical form is stored in `math`. The displayed
 * prompt is a proportion (not a linear equation), so the base's reparse
 * cross-check is disabled for this topic - correctness still rests on the
 * independent solve of the cross-multiplied equation.
 *
 * Families:
 *   P1: a/b = x/d          unknown numerator
 *   P2: a/b = c/x          unknown denominator
 *   P3: x/b = c/d          unknown numerator, left
 *   P4: (real-world ratio) worded proportion, unknown value
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr, LinearEquation } from "../engine/linear";
import type { Difficulty, GenerateContext, Generator, Question } from "./types";
import {
  EQUATION_VARIABLES,
  generateEquation,
  equationSignature,
  type BuiltEquation,
  type EquationTopicOptions,
} from "./equation-base";

const TOPIC = "proportions" as const;
const VERSION = "1.0.0";

type Family = "P1" | "P2" | "P3" | "P4";

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
  readonly solMin: number;
  readonly solMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["P1", "P3"], max: 8, solMin: 1, solMax: 10 },
  standard: { families: ["P1", "P2", "P3", "P4"], max: 12, solMin: 1, solMax: 12 },
  hard: { families: ["P2", "P4"], max: 15, solMin: 2, solMax: 15 },
  challenge: { families: ["P2", "P4"], max: 20, solMin: 2, solMax: 20 },
};

const CONTEXTS = [
  { unit: "miles", per: "hours" },
  { unit: "dollars", per: "items" },
  { unit: "pages", per: "minutes" },
  { unit: "cups", per: "servings" },
] as const;

function build(rng: Rng, difficulty: Difficulty): BuiltEquation | null {
  const cfg = DIFF[difficulty];
  const v = rng.pick(EQUATION_VARIABLES);
  const family = rng.pick(cfg.families);
  const s = rat(rng.int(cfg.solMin, cfg.solMax)); // proportions are positive

  // We construct a/b = c/d with exactly one entry being the variable, chosen
  // so cross multiplication holds at x = s.
  if (family === "P1" || family === "P3") {
    // x/b = c/d  with x = s  =>  choose d, then c = s*d/b must be integer.
    // Simpler: a/b = x/d form. Pick b, d; x = s; a = s*b/d must be integer.
    // Use scaling: ratio = s/d (unknown numerator over d). Set a/b equal.
    const d = rng.int(2, cfg.max);
    // Make a = s * b / d integer by choosing b as a multiple of d/gcd.
    const g = gcd(s.n < 0n ? -s.n : s.n, BigInt(d));
    const bMul = BigInt(d) / g;
    const bb = Number(bMul) * rng.int(1, 3);
    const aNum = (s.n * BigInt(bb)) / BigInt(d);
    if ((s.n * BigInt(bb)) % BigInt(d) !== 0n) return null;
    // Equation from cross-multiply of a/bb = x/d: a*d = bb*x -> bb*x = a*d
    const left = new LinearExpr(rat(bb), rat(0));
    const right = LinearExpr.constantExpr(rat(aNum * BigInt(d)));
    const eq = new LinearEquation(left, right);
    if (!eq.solve().value?.equals(s)) return null;
    const disp = `\\frac{${aNum.toString()}}{${bb}} = \\frac{${v}}{${d}}`;
    return mk(family, v, disp, `${bb}${v} = ${(aNum * BigInt(d)).toString()}`, eq, s,
      `Cross multiply: ${bb} \\cdot ${v} = ${aNum.toString()} \\cdot ${d}`);
  }

  // P2 / P4: a/b = c/x  =>  a*x = b*c  -> unknown denominator.
  const a = rng.int(2, cfg.max);
  const c = rng.int(2, cfg.max);
  // x = s means a*s = b*c, so choose b*c = a*s. Pick c, then need b = a*s/c int.
  if ((BigInt(a) * s.n) % BigInt(c) !== 0n) return null;
  const bVal = (BigInt(a) * s.n) / BigInt(c);
  if (bVal <= 0n) return null;
  // Equation: a*x = b*c
  const left = new LinearExpr(rat(a), rat(0));
  const right = LinearExpr.constantExpr(rat(bVal * BigInt(c)));
  const eq = new LinearEquation(left, right);
  if (!eq.solve().value?.equals(s)) return null;

  if (family === "P4") {
    const ctx = rng.pick(CONTEXTS);
    const disp = `\\frac{${a}\\text{ ${ctx.unit}}}{${bVal.toString()}\\text{ ${ctx.per}}} = \\frac{${c}\\text{ ${ctx.unit}}}{${v}\\text{ ${ctx.per}}}`;
    return mk(family, v, disp, `${a}${v} = ${(bVal * BigInt(c)).toString()}`, eq, s,
      `Cross multiply the ratio: ${a} \\cdot ${v} = ${bVal.toString()} \\cdot ${c}`);
  }
  const disp = `\\frac{${a}}{${bVal.toString()}} = \\frac{${c}}{${v}}`;
  return mk(family, v, disp, `${a}${v} = ${(bVal * BigInt(c)).toString()}`, eq, s,
    `Cross multiply: ${a} \\cdot ${v} = ${bVal.toString()} \\cdot ${c}`);
}

function mk(
  family: Family,
  v: string,
  displayLatex: string,
  displayText: string,
  eq: LinearEquation,
  s: ReturnType<typeof rat>,
  crossStep: string,
): BuiltEquation {
  return {
    family,
    variable: v,
    displayLatex,
    displayText,
    equation: eq,
    solution: s,
    leadingStep: { description: "Cross multiply to clear the fractions.", latex: crossStep },
    leadingHints: ["Cross multiply: the product of each diagonal is equal."],
  };
}

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x || 1n;
}

function maxMagnitude(difficulty: Difficulty): bigint {
  const m: Record<Difficulty, number> = { easy: 200, standard: 400, hard: 600, challenge: 900 };
  return BigInt(m[difficulty]);
}

const OPTIONS: EquationTopicOptions = {
  topic: TOPIC,
  version: VERSION,
  maxMagnitude,
  reparseCrossCheck: false, // displayed as a proportion, not a linear equation
  build,
};

export class ProportionsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];
  generate(ctx: GenerateContext): Question {
    return generateEquation(OPTIONS, ctx);
  }
  signature(q: Question) {
    return equationSignature(TOPIC, q);
  }
}

export const proportions = new ProportionsGenerator();
